import type { IntegrationItemDto } from '@fos/shared/contracts/integrations';
import {
  MAIL_BACKFILL_DAYS,
  MAIL_BACKFILL_MAX_MESSAGES,
  MAIL_MESSAGES,
  type MailBackfillDays,
  type MailboxHealthView,
} from '@fos/shared/contracts/mail';
import { SYNC_RUN_STATUS_LABELS } from '@fos/shared/labels';
import { History } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { formatDateShort, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Segmented } from '../../../ui/Segmented';
import { Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';

/**
 * „Stan skrzynki” i „Pobierz starsze maile” (DECISIONS 2026-10-17 [P] „Każda poczta w Inboxie, push tylko o
 * istotnych”, pkt 6): ostatnia synchronizacja, liczba wiadomości na serwerze (z ostatniej synchronizacji) i w systemie,
 * ostatni błąd i nieudane przebiegi z doby, pobieranie historii (30 / 90 / 365 dni — zadanie w workerze, stare maile
 * jako przeczytane „Mniej ważne”, bez powiadomień). Tylko osoba zarządzająca skrzynką (`item.canManage`).
 */

const DAYS_LABELS: Record<MailBackfillDays, string> = { 30: '30 dni', 90: '90 dni', 365: 'Rok' };

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-2.5">
      <dt className="shrink-0 text-body-s text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-right text-body-s break-words text-fg">{children}</dd>
    </div>
  );
}

function count(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = n === 1 ? one : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many;
  return `${n.toLocaleString('pl-PL')} ${word}`;
}

function MailboxHealth({ health }: { health: MailboxHealthView }) {
  const { imported, server, backfill } = health;
  return (
    <dl className="border-t border-line">
      <Row label="Synchronizacja">
        {health.lastSyncAt ? formatRelativePast(new Date(health.lastSyncAt)) : 'Jeszcze nie było'}
      </Row>
      {server && (
        <Row label="Na serwerze">
          {server.inbox === null ? '—' : count(server.inbox, 'wiadomość', 'wiadomości', 'wiadomości')} w Odebranych
          {server.sent !== null && (
            <span className="text-fg-muted"> · {server.sent.toLocaleString('pl-PL')} w Wysłanych</span>
          )}
        </Row>
      )}
      <Row label="W systemie">
        {count(imported.received, 'odebrana', 'odebrane', 'odebranych')}
        <span className="text-fg-muted"> · {imported.sent.toLocaleString('pl-PL')} wysłanych</span>
        {imported.oldest && (
          <span className="block text-caption text-fg-muted">od {formatDateShort(new Date(imported.oldest))}</span>
        )}
      </Row>
      {health.lastFailure && (
        <Row label="Ostatni błąd">
          <span className={health.status === 'error' ? 'text-danger' : 'text-fg-secondary'}>
            {health.lastFailure.error}
          </span>
          <span className="block text-caption text-fg-muted">
            {formatRelativePast(new Date(health.lastFailure.at))}
            {health.failuresLastDay > 1 &&
              ` · ${count(health.failuresLastDay, 'nieudany', 'nieudane', 'nieudanych')} w ciągu doby`}
          </span>
        </Row>
      )}
      {backfill && (
        <Row label="Starsze maile">
          {backfill.status === 'running'
            ? 'Pobieranie w toku…'
            : backfill.status === 'failed'
              ? `${SYNC_RUN_STATUS_LABELS.failed}: ${backfill.error ?? ''}`
              : `Pobrane ${formatRelativePast(new Date(backfill.finishedAt ?? backfill.startedAt))}`}
        </Row>
      )}
    </dl>
  );
}

function BackfillDialog({
  open,
  onOpenChange,
  health,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  health: MailboxHealthView;
}) {
  const utils = trpc.useUtils();
  const [days, setDays] = useState<MailBackfillDays>(30);
  const request = trpc.mail.requestBackfill.useMutation({
    onSuccess: () => {
      toast.success(MAIL_MESSAGES.backfillQueued);
      onOpenChange(false);
      void utils.mail.health.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zlecić pobierania.')),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Pobierz starsze maile"
      description={`Starsze maile z ${health.address} trafią do Inboxu jako przeczytane („Mniej ważne”), bez powiadomień. Te, które już są, zostaną pominięte.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={request.isPending}
            onClick={() => request.mutate({ mailboxId: health.mailboxId, days })}
          >
            Pobierz
          </Button>
        </>
      }
    >
      <Field
        label="Z ostatnich"
        hint={`Najwyżej ${MAIL_BACKFILL_MAX_MESSAGES.toLocaleString('pl-PL')} najnowszych wiadomości z Odebranych i Wysłanych.`}
      >
        <Segmented<`${MailBackfillDays}`>
          aria-label="Okres"
          fullWidth
          value={`${days}`}
          onValueChange={(v) => setDays(Number(v) as MailBackfillDays)}
          options={MAIL_BACKFILL_DAYS.map((d) => ({ value: `${d}` as const, label: DAYS_LABELS[d] }))}
        />
      </Field>
    </Dialog>
  );
}

export function MailboxHealthSection({ item }: { item: IntegrationItemDto }) {
  const [open, setOpen] = useState(false);
  const enabled = Boolean(item.id) && item.canManage && item.status !== 'disconnected';
  const query = trpc.mail.health.useQuery({ integrationId: item.id ?? '' }, { enabled, refetchInterval: 30_000 });
  if (!enabled) return null;
  if (query.isPending) {
    return (
      <div className="mt-6 flex flex-col gap-2" aria-busy="true">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }
  if (query.isError || query.data.length === 0) return null;
  return (
    <section aria-labelledby="mailbox-health" className="mt-6">
      <h3 id="mailbox-health" className="mb-2 text-body-s font-medium text-fg">
        Stan skrzynki
      </h3>
      {query.data.map((health) => (
        <div key={health.mailboxId}>
          <MailboxHealth health={health} />
          {health.canBackfill && (
            <>
              <Button variant="secondary" icon={History} className="mt-3 w-full" onClick={() => setOpen(true)}>
                Pobierz starsze maile
              </Button>
              <BackfillDialog open={open} onOpenChange={setOpen} health={health} />
            </>
          )}
        </div>
      ))}
    </section>
  );
}
